import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createList } from '../firebase/database';
import { Gift, Users, ArrowLeft, Globe } from 'lucide-react';
import { SUPPORTED_CURRENCIES, detectUserCurrency, getCurrencySymbol } from '../services/currencyService';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTranslation } from '../hooks/useTranslation';

const CreateList = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isGroup: false,
    occasion: '',
    eventDate: '',
    defaultCurrency: 'NOK'
  });
  const [loading, setLoading] = useState(false);
  const [detectingCurrency, setDetectingCurrency] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const detectCurrency = async () => {
      setDetectingCurrency(true);
      try {
        const detectedCurrency = await detectUserCurrency();
        setFormData(prev => ({
          ...prev,
          defaultCurrency: detectedCurrency.code
        }));
      } catch (error) {
        console.error('Failed to detect currency:', error);
      }
      setDetectingCurrency(false);
    };

    detectCurrency();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(t('createList.enterListName'));
      return;
    }

    if (!user) {
      toast.error(t('createList.mustBeLoggedIn'));
      return;
    }

    setLoading(true);

    const listData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      isGroup: formData.isGroup,
      occasion: formData.occasion.trim(),
      eventDate: formData.eventDate || null,
      defaultCurrency: formData.defaultCurrency,
      createdBy: user.uid,
      members: [user.uid],
      wishCount: 0
    };

    const result = await createList(listData);
    
    if (result.success) {
      toast.success(t('createList.listCreatedSuccess'));
      navigate(`/list/${result.id}/view`);
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  if (loading) {
    return <LoadingSpinner text={t('createList.creatingYourList')} />;
  }

  return (
    <div className="max-w-2xl mx-auto px-2 sm:px-0">
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{t('createList.backToDashboard')}</span>
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
          {t('createList.title')}
        </h1>
        <p className="text-sm sm:text-base text-gray-600">
          {t('createList.subtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Gift className="h-5 w-5 mr-2 text-primary-600" />
            {t('createList.basicInfo')}
          </h2>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('createList.listName')} *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="input-field"
                placeholder={t('createList.listNamePlaceholder')}
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                {t('createList.description')}
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                className="input-field"
                placeholder={t('createList.descriptionPlaceholder')}
                value={formData.description}
                onChange={handleChange}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="occasion" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('createList.occasion')}
                </label>
                <select
                  id="occasion"
                  name="occasion"
                  className="input-field"
                  value={formData.occasion}
                  onChange={handleChange}
                >
                  <option value="">{t('createList.selectOccasion')}</option>
                  <option value="Christmas">{t('createList.occasions.Christmas')}</option>
                  <option value="Birthday">{t('createList.occasions.Birthday')}</option>
                  <option value="Wedding">{t('createList.occasions.Wedding')}</option>
                  <option value="Anniversary">{t('createList.occasions.Anniversary')}</option>
                  <option value="Graduation">{t('createList.occasions.Graduation')}</option>
                  <option value="Baby Shower">{t('createList.occasions.Baby Shower')}</option>
                  <option value="Other">{t('createList.occasions.Other')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('createList.eventDate')}
                </label>
                <input
                  type="date"
                  id="eventDate"
                  name="eventDate"
                  className="input-field"
                  value={formData.eventDate}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="defaultCurrency" className="block text-sm font-medium text-gray-700 mb-1">
                <Globe className="h-4 w-4 inline mr-1" />
                {t('createList.defaultCurrency')}
                {detectingCurrency && <span className="text-sm text-gray-500 ml-2">({t('createList.detecting')})</span>}
              </label>
              <select
                id="defaultCurrency"
                name="defaultCurrency"
                className="input-field"
                value={formData.defaultCurrency}
                onChange={handleChange}
              >
                {SUPPORTED_CURRENCIES.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {getCurrencySymbol(currency.code)} {currency.name} ({currency.code})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {t('createList.currencyHelp')}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2 text-secondary-600" />
            {t('createList.sharingOptions')}
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="isGroup"
                name="isGroup"
                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={formData.isGroup}
                onChange={handleChange}
              />
              <div className="flex-1">
                <label htmlFor="isGroup" className="text-sm font-medium text-gray-700">
                  {t('createList.makeGroupList')}
                </label>
                <p className="text-sm text-gray-500 mt-1">
                  {t('createList.groupListDesc')}
                </p>
              </div>
            </div>

            {formData.isGroup && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm text-blue-800 font-medium">{t('createList.groupListFeatures')}</p>
                    <ul className="text-sm text-blue-700 mt-1 space-y-1">
                      <li>• {t('createList.groupListFeature1')}</li>
                      <li>• {t('createList.groupListFeature2')}</li>
                      <li>• {t('createList.groupListFeature3')}</li>
                      <li>• {t('createList.groupListFeature4')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="btn-secondary"
          >
            {t('createList.cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? t('createList.creating') : t('createList.createList')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateList;
